import { TourInfoScreenEnum } from '@aorg/models/enums/tours/TourInfoScreenEnum';
import { TourTypeEnum } from '@aorg/models/enums/tours/TourTypeEnum';
import {
  ShowTour,
  selectToursList,
  useAppDispatch,
  useAppSelector,
} from '@aorg/store';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

// The page each tour info screen belongs to. Detail pages share the screen of
// their section, and the more specific paths come first so /network does not
// take over /network/mentors.
const SCREEN_PATHS: [string, TourInfoScreenEnum][] = [
  ['/resources/chat', TourInfoScreenEnum.QAi],
  ['/resources/document-review', TourInfoScreenEnum.DocumentReview],
  ['/resources/exclusive-deals', TourInfoScreenEnum.ExcluisveDeals],
  ['/resources/background-checks', TourInfoScreenEnum.BackgroundChecks],
  ['/network/expert-network', TourInfoScreenEnum.ExpertNetwork],
  ['/network/agencies-collectives', TourInfoScreenEnum.AgenciesCollectives],
  ['/network/mentors', TourInfoScreenEnum.Mentors],
  ['/network', TourInfoScreenEnum.Network],
  ['/survey/school-ratings', TourInfoScreenEnum.Survey],
  ['/discounts', TourInfoScreenEnum.Discounts],
];

export function tourInfoScreenFor(
  pathname: string
): TourInfoScreenEnum | undefined {
  if (pathname === '/') return TourInfoScreenEnum.Home;

  return SCREEN_PATHS.find(
    ([path]) => pathname === path || pathname.startsWith(`${path}/`)
  )?.[1];
}

/**
 * The Welcome tour steps of the page the user is on, picked by their info
 * screen, and the action that opens them. Pages without an info screen have
 * no tour.
 */
export function usePageTour() {
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const allTours = useAppSelector(selectToursList);
  const screen = tourInfoScreenFor(pathname);

  const tours = useMemo(
    () =>
      screen === undefined
        ? []
        : (allTours ?? []).filter(
            (it) =>
              it.type === TourTypeEnum.Welcome && it.info_screen === screen
          ),
    [allTours, screen]
  );

  const showTour = useCallback(() => {
    if (screen === undefined) return;

    dispatch(
      ShowTour({
        isFirst: false,
        TypeSelected: TourTypeEnum.Welcome,
        ScreenSelected: screen,
      })
    );
  }, [dispatch, screen]);

  return { screen, tours, showTour };
}
